import { state, getCfg } from './state.js';
import { paintScene } from './paint-engine.js';
import { trackEvent } from './supabase-client.js';

/* ═══════════════════════════════════════════
   § 8. UI & POPUP LOGIC
   initUI() wires all button events and returns
   { renderCard, selectPlot, deselectAll } so
   main.js can pass them as callbacks to other
   modules (data-sync, camera-controls, nearby-locations).
   ═══════════════════════════════════════════ */

export function initUI(ctx, { flyTo }) {
  const { scene, camera, controls } = ctx;
  const $ = id => document.getElementById(id);

  // ── Area unit — reads at call-time so polling updates are reflected ──
  // Falls back to 'SQ FT' for any legacy project without area_unit set.
  const getAreaUnit = () => state.uiSettings.area_unit || 'SQ FT';

  // ── renderCard ─────────────────────────────
  function renderCard(pid) {
    const p = state.crmData.find(d => d.Plot_ID === pid);
    if (!p) return;

    const nm = p.Plot_ID.replace('Plot-', '');
    $('cTitle').textContent = 'PLOT ' + nm;
    $('cSec').textContent   = (p.Sector || '—').replace('_', ' ').toUpperCase();
    $('cArea').textContent  = p.Area ? p.Area + ' ' + getAreaUnit() : 'TBD';
    $('cFace').textContent  = p.Facing || '—';
    $('cPrice').textContent = p.Price ? '₹ ' + Number(p.Price).toLocaleString('en-IN') : 'CALL';

    const b = $('cBadge');
    b.textContent = p.Status;
    b.className   = 'badge ' + p.Status.toLowerCase();
    state.waMsg   = `Hi, I am interested in Plot ${nm}. Is it still ${p.Status}?`;

    if (getCfg('enablePopupCard') !== false) {
      $('card').classList.remove('hide');
      $('cPrice').parentNode.style.display = getCfg('showPrice')   !== false ? 'flex' : 'none';
      $('cArea').parentNode.style.display  = getCfg('showArea')    !== false ? 'flex' : 'none';
      $('cSec').parentNode.style.display   = getCfg('showSector')  !== false ? 'flex' : 'none';
      $('cFace').parentNode.style.display  = getCfg('showFacing')  !== false ? 'flex' : 'none';
      b.style.display = getCfg('showStatus') !== false ? 'inline-block' : 'none';

      // ── Sector progress bar ──
      const sectorId      = p.sector_id;
      const sectorPlots   = state.crmData.filter(d => d.sector_id === sectorId);
      const soldInSector  = sectorPlots.filter(d => d.status_id === 3).length;
      const pct = sectorPlots.length > 0 ? Math.round(soldInSector / sectorPlots.length * 100) : 0;
      $('cSectorBar').style.width = pct + '%';

      // ── Scarcity badge ──
      const existingTag = $('card').querySelector('.rarity-tag');
      if (existingTag) existingTag.remove();
      if (p.area) {
        const sameSize = state.crmData.filter(d => d.area === p.area && d.status_id === 1);
        if (sameSize.length > 0 && sameSize.length <= 3) {
          const tag = document.createElement('span');
          tag.className = 'rarity-tag';
          tag.innerHTML = `<svg width="8" height="8" viewBox="0 0 24 24" fill="#b8944e"><circle cx="12" cy="12" r="10"/></svg> Only ${sameSize.length} left in this size`;
          $('card').querySelector('h2').after(tag);
        }
      }
    } else {
      $('card').classList.add('hide');
    }
  }

  // ── selectPlot ─────────────────────────────
  function selectPlot(id) {
    deselectAll();
    state.selectedPlotId = id;
    $('vig').classList.add('on');
    renderCard(id);

    const p = state.crmData.find(d => d.Plot_ID === id);
    if (p) {
      $('fl-t').textContent = 'PLOT ' + id.replace('Plot-', '');

      let parts = [];
      if (getCfg('hoverShowSector')  !== false && p.Sector) parts.push(p.Sector.replace('_', ' ').toUpperCase());
      if (getCfg('hoverShowArea')    !== false && p.Area)   parts.push(p.Area + ' ' + getAreaUnit());
      if (getCfg('hoverShowFacing')  !== false && p.Facing) parts.push(p.Facing);

      $('fl-d').textContent    = parts.length > 0 ? parts.join(' | ') : '';
      $('fl-d').style.display  = parts.length > 0 ? 'block' : 'none';

      let showHover = true;
      if (getCfg('enableHoverPopup') === false || getCfg('showHoverPopup') === false || getCfg('enableFloatingLabel') === false) {
        showHover = false;
      }
      if (showHover) $('fl').classList.add('show');
    }
    paintScene(true);
  }

  // ── deselectAll ────────────────────────────
  function deselectAll() {
    $('vig').classList.remove('on');
    state.selectedPlotId     = null;
    state.selectedPlotCenter = null;
    $('card').classList.add('hide');
    $('fl').classList.remove('show');

    if (state.currentPath) {
      scene.remove(state.currentPath);
      if (state.currentPath.geometry) state.currentPath.geometry.dispose();
      if (state.currentPath.material) state.currentPath.material.dispose();
      state.currentPath = null;
    }
    if (state.currentPin) {
      scene.remove(state.currentPin);
      if (state.currentPin.material) {
        if (state.currentPin.material.map) state.currentPin.material.map.dispose();
        state.currentPin.material.dispose();
      }
      state.currentPin = null;
    }
    if (state.currentRipple) {
      scene.remove(state.currentRipple);
      if (state.currentRipple.geometry) state.currentRipple.geometry.dispose();
      if (state.currentRipple.material) state.currentRipple.material.dispose();
      state.currentRipple = null;
    }
    if (state.pathAnimFrame) { cancelAnimationFrame(state.pathAnimFrame); state.pathAnimFrame = null; }
    paintScene(true);
  }

  // ── toggleMap ──────────────────────────────
  function toggleMap() {
    state.mapOn = !state.mapOn;
    $('mapov').classList.toggle('off', !state.mapOn);
    $('btn-2d').classList.toggle('on', state.mapOn);
    if (state.mapOn) deselectAll();
  }

  // ── Button: STATUS ─────────────────────────
  $('btn-status').addEventListener('click', () => {
    if (state.mapOn) { toggleMap(); return; }
    state.showStatus = !state.showStatus;
    if (state.showStatus) {
      state.showSector = false;
      $('btn-sectors').classList.remove('on');
      $('legSec').classList.remove('on');
    }
    $('btn-status').classList.toggle('on', state.showStatus);
    $('leg').classList.toggle('on', state.showStatus);
    paintScene();
  });

  // ── Button: SECTORS ────────────────────────
  $('btn-sectors').addEventListener('click', () => {
    if (state.mapOn) { toggleMap(); return; }
    state.showSector = !state.showSector;
    if (state.showSector) {
      state.showStatus = false;
      $('btn-status').classList.remove('on');
      $('leg').classList.remove('on');
    }
    $('btn-sectors').classList.toggle('on', state.showSector);
    $('legSec').classList.toggle('on', state.showSector);
    paintScene();
  });


  // ── Button: 3D / TOP VIEW ──────────────────
  // Helper: sync icon & label to TARGET state (what clicking will do next).
  // When in 3D view  → next click goes to Top View → show TOP VIEW label + flat icon.
  // When in Top view → next click goes to 3D view  → show 3D VIEW  label + cube icon.
  function syncViewBtn() {
    const viewIcon  = $('view-icon');
    const viewLabel = $('view-label');
    if (!viewIcon || !viewLabel) return;

    // This button is a transient action (camera reset), not a persistent mode.
    // Strip the .on class unconditionally so it never retains an active background.
    $('btn-3d').classList.remove('on');

    if (state.is3DView) {
      // Currently in 3D → target action is Top View
      viewIcon.innerHTML    = '<rect x="3" y="3" width="18" height="18" rx="2" ry="2"/><circle cx="12" cy="12" r="3"/>';
      viewLabel.textContent = 'TOP VIEW';
    } else {
      // Currently in Top View → target action is 3D View
      viewIcon.innerHTML    = '<polygon points="12 2 2 7 12 12 22 7 12 2"/><polyline points="2 17 12 22 22 17"/><polyline points="2 12 12 17 22 12"/>';
      viewLabel.textContent = '3D VIEW';
    }
  }

  // Initialise button to the correct target-state label on first load.
  // (App starts in 3D view, so button should read "TOP VIEW" from the start.)
  syncViewBtn();

  $('btn-3d').addEventListener('click', () => {
    if (state.mapOn) toggleMap();
    deselectAll();

    // 1. Flip the view state
    state.is3DView = !state.is3DView;

    // 2. Update label/icon to reflect the NEW target (what next click will do)
    syncViewBtn();

    // 3. Trigger smooth camera transition to the default position for this view
    state.startCamPos.copy(camera.position);
    state.startCtrlTarget.copy(controls.target);
    state.startCamQuat.copy(camera.quaternion);

    state.animTarget       = state.baseCenter.clone();
    state.animCameraTarget = state.is3DView ? state.default3DPos.clone() : state.defaultTopPos.clone();

    // Calculate final quaternion for the destination camera position
    const tempCam = camera.clone();
    tempCam.position.copy(state.animCameraTarget);
    tempCam.lookAt(state.animTarget);
    state.endCamQuat.copy(tempCam.quaternion);

    state.animDurationScale = 1.6;
    controls.enabled        = false;
    state.isAnimatingCamera = true;
    state.animTime          = 0.01;
  });

  // ── Button: LIVE MAP ───────────────────────
  $('btn-2d').addEventListener('click', toggleMap);

  // ── Close card / WhatsApp inquire ──────────
  $('cx').addEventListener('click', deselectAll);
  $('inq').addEventListener('click', () => {
    // ANALYTICS: whatsapp_click — fire-and-forget, never awaited
    trackEvent('whatsapp_click', state.selectedPlotId);
    window.open(`https://wa.me/${state.waNumber}?text=` + encodeURIComponent(state.waMsg), '_blank');
  });

  // ── Share current plot ──────────────────────
  async function shareCurrentPlot(plotId) {
    if (!plotId) return;
    const plotNum  = plotId.replace('Plot-', '');
    const shareUrl = window.location.origin + window.location.pathname + '?plot=' + plotId;
    const shareData = {
      title: `Plot ${plotNum} — PlotOs`,
      text:  `Check out Plot ${plotNum} on the interactive map!`,
      url:   shareUrl,
    };

    try {
      if (navigator.share && navigator.canShare && navigator.canShare(shareData)) {
        await navigator.share(shareData);
      } else {
        // Fallback: copy link to clipboard
        await navigator.clipboard.writeText(shareUrl);
        alert(`Link copied!\n${shareUrl}`);
      }
    } catch (err) {
      // User dismissed the share sheet — not an error worth surfacing
      if (err.name !== 'AbortError') {
        // Last-resort fallback (very old browsers without clipboard API)
        prompt('Copy this link:', shareUrl);
      }
    }
  }

  $('btn-share-plot').addEventListener('click', () => {
    shareCurrentPlot(state.selectedPlotId);
  });

  // ── Search box ─────────────────────────────
  $('sBtn').addEventListener('click', () => {
    const v = $('sInp').value.trim();
    if (!v) return;
    const n = v.replace(/\D/g, '');
    if (!n) return;

    const p3 = n.padStart(3, '0');
    const p2 = n.padStart(2, '0');

    // 1. Try exact matches first
    let p = state.crmData.find(d => d.Plot_ID && (
      d.Plot_ID === `Plot-${p3}` || d.Plot_ID === `Plot-${p2}` || d.Plot_ID === `Plot-${n}`
    ));

    // 2. Fallback to suffix matching (avoids matching "15" when searching "5")
    if (!p) {
      p = state.crmData.find(d => d.Plot_ID && (
        d.Plot_ID.endsWith(`-${p3}`) || d.Plot_ID.endsWith(`-${p2}`) || d.Plot_ID.endsWith(`-${n}`)
      ));
    }

    // 3. Ultimate fallback to includes
    if (!p) p = state.crmData.find(d => d.Plot_ID && d.Plot_ID.includes(n));

    if (p) {
      if (state.mapOn) toggleMap();
      selectPlot(p.Plot_ID);
      if (p.MatName) flyTo(p.MatName, ctx);
    }
  });
  $('sInp').addEventListener('keypress', e => { if (e.key === 'Enter') $('sBtn').click(); });

  return { renderCard, selectPlot, deselectAll, toggleMap };
}
